import { debounce, isEqual } from 'lodash';
import { mapGetters } from 'vuex';
import { PROMOTION_TYPE } from '@/views/config/coupon/helper/constant';
import { logger } from '@/utils/log';

const promotionLogPrefix = 'promotion-watch-fingerprint';

/**
 * Global promotion instance manager for handling multiple promotion types
 * Manages instance registration, pending actions, and auto-verification execution
 */
export const promotionInstanceInfo = {
  // Store promotion instances by type
  [PROMOTION_TYPE.coupon]: null,
  [PROMOTION_TYPE.pureReferralCoupon]: null,

  // Store pending actions for each promotion type
  pendingActions: {
    [PROMOTION_TYPE.coupon]: null,
    [PROMOTION_TYPE.pureReferralCoupon]: null,
  },

  // Track instance readiness status
  instanceReady: {
    [PROMOTION_TYPE.coupon]: false,
    [PROMOTION_TYPE.pureReferralCoupon]: false,
  },

  /**
   * Register a promotion instance and execute any pending actions
   * @param {string} promotionType - The promotion type (from PROMOTION_TYPE)
   * @param {Object} instance - The Vue component instance with autoVerifyAction method
   */
  setPromotionInstance(promotionType, instance) {
    if (!promotionType || !instance) {
      logger.warn(`${promotionLogPrefix} Invalid promotion instance registration:`, { promotionType, instance });
      return;
    }

    this[promotionType] = instance;
    this.instanceReady[promotionType] = true;

    // Execute any pending actions for this instance
    this.executePendingAction(promotionType);
  },

  /**
   * Remove a promotion instance and clean up related resources
   * @param {string} promotionType - The promotion type (from PROMOTION_TYPE)
   */
  removePromotionInstance(promotionType) {
    if (!promotionType) {
      logger.warn(`${promotionLogPrefix} Invalid promotion type for removal:`, { promotionType });
      return;
    }

    // Clear the instance reference
    this[promotionType] = null;
    this.instanceReady[promotionType] = false;

    // Clear any pending actions for this promotion type
    this.pendingActions[promotionType] = null;

    logger.log(`${promotionLogPrefix} ${promotionType} instance removed and cleaned up`);
  },

  /**
   * Set a pending action for a specific promotion type
   * @param {string} promotionType - The promotion type
   * @param {Function} action - The action to execute when instance is ready
   */
  setPendingAction(promotionType, action) {
    if (!promotionType || typeof action !== 'function') {
      logger.warn(`${promotionLogPrefix} Invalid pending action:`, { promotionType, action });
      return;
    }

    this.pendingActions[promotionType] = action;
  },

  /**
   * Execute pending action for a specific promotion type
   * @param {string} promotionType - The promotion type
   */
  executePendingAction(promotionType) {
    const pendingAction = this.pendingActions[promotionType];
    if (pendingAction) {
      try {
        pendingAction();
        this.pendingActions[promotionType] = null; // Clear after execution
      } catch (error) {
        logger.error(`${promotionLogPrefix} Error executing pending action for ${promotionType}:`, error);
        this.pendingActions[promotionType] = null; // Clear even on error
      }
    }
  },

  /**
   * Check if a promotion instance is ready
   * @param {string} promotionType - The promotion type
   * @returns {boolean} True if instance is ready
   */
  isInstanceReady(promotionType) {
    return Boolean(this.instanceReady[promotionType]);
  },

  /**
   * Execute auto-verification for a specific instance
   * @private
   * @param {string} promotionType - The promotion type
   */
  _executeInstanceAutoVerify(promotionType) {
    const instance = this[promotionType];
    if (!instance || typeof instance.autoVerifyAction !== 'function') {
      logger.warn(`${promotionLogPrefix} Invalid instance or missing autoVerifyAction for ${promotionType}`);
      return;
    }

    try {
      instance.autoVerifyAction();
      logger.log(`${promotionLogPrefix} ${promotionType} autoVerifyAction executed successfully`);
    } catch (error) {
      logger.error(`${promotionLogPrefix} Error executing autoVerifyAction for ${promotionType}:`, error);
    }
  },

  /**
   * Set pending auto-verify actions for all instances
   * Used when shopping cart changes and all instances need to re-verify
   */
  setPendingAutoVerifyForAllInstances: debounce(function () {
    Object.keys(this.instanceReady).forEach(promotionType => {
      if (this.isInstanceReady(promotionType)) {
        logger.log(`${promotionLogPrefix} ${promotionType} instance ready!`);
        this._executeInstanceAutoVerify(promotionType);
      } else {
        logger.log(`${promotionLogPrefix} ${promotionType} instance not ready, queuing auto-verify action...`);
        this.setPendingAction(promotionType, () => {
          this._executeInstanceAutoVerify(promotionType);
        });
      }
    });
  }, 500)
};

/**
 * Promotion mixin for Vue components
 * Provides computed properties and methods for managing promotion instances
 * Watches shopping cart changes and triggers auto-verification for all promotion instances
 */
export default {
  computed: {
    ...mapGetters([
      'computedAmountLoading',
      'promotionInfo',
      'cartIsReady',
      'getSelectedPlansWithQuantityMap',
      'getSelectedFreeTrialPlansWithQuantityMap',
      'soldToContact',
      'addressInfo'
    ]),

    /**
     * Computed property that combines all shopping cart related data
     * Used for deep watching to detect cart changes
     * @returns {Object} Combined cart data object
     */
    watchFingerprint() {
      return {
        cartIsReady: this.cartIsReady,
        getSelectedPlansWithQuantityMap: this.getSelectedPlansWithQuantityMap,
        getSelectedFreeTrialPlansWithQuantityMap: this.getSelectedFreeTrialPlansWithQuantityMap,
        soldToContactCountry: this.soldToContact.country,
        usageType: this.addressInfo.usageType,
      };
    },
  },

  methods: {
    /**
     * Trigger auto-verification for all promotion instances
     * Sets pending actions for instances that are not ready yet
     */
    setPendingAutoVerifyForAllInstances() {
      promotionInstanceInfo.setPendingAutoVerifyForAllInstances();
    }
  },

  watch: {
    /**
     * Watch for shopping cart changes and trigger promotion verification
     * Only triggers when actual changes are detected to avoid unnecessary API calls
     */
    watchFingerprint: {
      deep: true,
      handler(newVal, oldVal) {
        // Check if shopping cart has actually changed
        // This includes adding/removing products or changing plan quantities
        const hasCartChanged = !isEqual(newVal, oldVal);

        // Skip if no changes detected to avoid unnecessary API calls
        if (!hasCartChanged) {
          return;
        }

        // skip if cart is not ready
        if (!newVal.cartIsReady) {
          return;
        }

        // Trigger auto-verification for all promotion instances
        // Each instance will only execute the latest verification request when ready
        this.setPendingAutoVerifyForAllInstances();
      },
    },
  },
};
